import type { WatchBuildResult, WatchPart } from '../api/client'
import {
  buildCartPurchaseUrl,
  buildPartPurchaseUrl,
  canBuyFullBuild,
  partHasPurchaseLink,
  providerDisplayName,
  variantIdForPart,
} from '../outbound/links'
import { AssembledPreview } from './AssembledPreview'
import { PartCard } from './PartCard'
import { UnfulfilledWishCard } from './UnfulfilledWishCard'
import {
  CORE_PART_COUNT,
  crownMismatch,
  decisionsByType,
  formatCurrency,
  sortParts,
} from './buildHelpers'

type BuildSummaryProps = {
  buildResult: WatchBuildResult | null
  updatedParts?: Set<string>
  onSuggestionClick?: (suggestion: string) => void
  isSending?: boolean
  isGeneratingImage?: boolean
  assembledImageUrl?: string | null
  imageError?: string | null
  onGenerateImage?: () => void
  conversationId?: string
  onPartPurchaseClick?: (part: WatchPart, partType: string) => void
  onBuyFullBuildClick?: (parts: WatchPart[], total: number) => void
  onCaseVariantChange?: (variantId: string) => void
}

export function BuildSummary({
  buildResult,
  updatedParts,
  onSuggestionClick,
  isSending = false,
  isGeneratingImage = false,
  assembledImageUrl = null,
  imageError = null,
  onGenerateImage,
  conversationId,
  onPartPurchaseClick,
  onBuyFullBuildClick,
  onCaseVariantChange,
}: BuildSummaryProps) {
  if (!buildResult) {
    return (
      <section className="build-summary empty-build" aria-labelledby="build-heading">
        <p className="results-eyebrow">Live build</p>
        <h2 id="build-heading" className="results-title">
          Your selected parts will appear here.
        </h2>
        <p className="size-reason">
          Start a conversation and Horatix will assemble compatible case, movement, dial, hands, and
          strap options with transparent pricing.
        </p>
      </section>
    )
  }

  const parts = sortParts(buildResult.build.parts)
  const decisions = decisionsByType(buildResult.decisions)
  const fallbackCurrency = parts[0]?.[1].currency ?? 'USD'
  const total = parts.reduce((sum, [, part]) => sum + part.price, 0)
  const hasFailures = buildResult.failed_steps.length > 0
  const filledCount = parts.length
  const totalSlots = filledCount + buildResult.failed_steps.length
  const isIncomplete =
    hasFailures || (filledCount > 0 && filledCount < CORE_PART_COUNT)
  const partList = parts.map(([, part]) => part)
  const buildPartIds = partList.map((part) => part.id)
  const showBuyFullBuild = canBuyFullBuild(partList)
  const buyFullBuildProvider = showBuyFullBuild ? providerDisplayName(partList[0].provider_id) : null
  const buyFullBuildHref = showBuyFullBuild
    ? buildCartPurchaseUrl({
        partIds: buildPartIds,
        conversationId,
        variantIds: partList.map((part) => variantIdForPart(part)),
      })
    : null
  const hasAnyPurchaseLinks = partList.some(partHasPurchaseLink)
  const crownMismatchMessage = crownMismatch(parts)

  return (
    <section className="build-summary" aria-labelledby="build-heading">
      <div className="results-header">
        <div>
          <p className="results-eyebrow">
            {isIncomplete ? 'Build in progress' : 'Selection complete'}
          </p>
          <h2 id="build-heading" className="results-title">
            {hasFailures
              ? `Horatix selected ${parts.length} part${parts.length !== 1 ? 's' : ''} — ${buildResult.failed_steps.length} slot${buildResult.failed_steps.length === 1 ? '' : 's'} still need compatible parts`
              : isIncomplete
                ? `Horatix selected ${parts.length} of ${CORE_PART_COUNT} core parts so far.`
                : `Horatix selected ${parts.length} compatible part${parts.length !== 1 ? 's' : ''}.`}
          </h2>
          {buildResult.watch_size ? (
            <p className="size-reason">{buildResult.watch_size.reason}</p>
          ) : null}
        </div>
        {buildResult.watch_size ? (
          <div className="size-badge">
            <span className="size-label">Watch size</span>
            <span className="size-value">{buildResult.watch_size.selected_size}</span>
            <span className="size-range">
              {buildResult.watch_size.min_case_diameter} – {buildResult.watch_size.max_case_diameter} mm
            </span>
          </div>
        ) : null}
      </div>

      {buildResult.compatibility_errors.length > 0 ? (
        <div className="compatibility-alert">
          <strong>Compatibility notes</strong>
          {buildResult.compatibility_errors.map((compatError) => (
            <p key={compatError}>{compatError}</p>
          ))}
        </div>
      ) : null}

      {crownMismatchMessage ? (
        <div className="compatibility-alert">
          <strong>Crown position mismatch</strong>
          <p>{crownMismatchMessage}</p>
        </div>
      ) : null}

      {onGenerateImage ? (
        <AssembledPreview
          buildResult={buildResult}
          isSending={isSending}
          isGenerating={isGeneratingImage}
          imageUrl={assembledImageUrl}
          error={imageError}
          onGenerate={onGenerateImage}
        />
      ) : null}

      <div className="parts-section">
        <div className="parts-section-header">
          <span className="parts-section-title">Selected Parts</span>
          <span className="parts-count">
            {filledCount} of {Math.max(totalSlots, CORE_PART_COUNT)} compatible
          </span>
        </div>

        <div className="parts-grid">
          {parts.map(([partType, part]) => (
            <PartCard
              key={part.id}
              partType={partType}
              part={part}
              decision={decisions[partType]}
              isUpdated={updatedParts?.has(partType) ?? false}
              purchaseHref={
                partHasPurchaseLink(part)
                  ? buildPartPurchaseUrl({
                      partId: part.id,
                      source: 'part_card',
                      conversationId,
                      buildPartIds,
                      variantId: variantIdForPart(part),
                    })
                  : null
              }
              onPurchaseClick={() => onPartPurchaseClick?.(part, partType)}
              onCaseVariantChange={partType === 'case' ? onCaseVariantChange : undefined}
            />
          ))}

          {buildResult.failed_steps.map((failure) => (
            <UnfulfilledWishCard
              key={failure.part_type}
              failure={failure}
              onSuggestionClick={onSuggestionClick}
            />
          ))}
        </div>
      </div>

      {parts.length > 0 && (
        <div className="total-bar">
          <div>
            <p className="total-label">Total build cost</p>
            <p className="total-amount">{formatCurrency(total, fallbackCurrency)}</p>
            {!showBuyFullBuild && hasAnyPurchaseLinks ? (
              <p className="total-buy-hint">Buy parts individually below.</p>
            ) : null}
          </div>
          <div className="total-bar-actions">
            <p className="total-meta">
              {parts.length} part{parts.length !== 1 ? 's' : ''}
            </p>
            {showBuyFullBuild && buyFullBuildHref ? (
              <a
                className="btn-buy-build"
                href={buyFullBuildHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onBuyFullBuildClick?.(partList, total)}
              >
                Buy full build at {buyFullBuildProvider}
              </a>
            ) : null}
          </div>
        </div>
      )}
    </section>
  )
}
